import { auth } from 'src/services/firebase-compat'
import { getSession } from 'src/utils/functions/localStorage'
import { getModuleFromPathname } from 'src/utils/functions/moduleUtils'
import { LoopModule } from './openApiV2'

export interface ErrorLogEntry {
  email: string | null
  apiUrl: string | null
  pageUrl: string
  errorMessage: string
  statusCode: number | null
  errorType: 'client' | 'api' | 'network' | 'unhandled' | 'unknown'
  timestamp: string
  userAgent: string
  module: string
  org: string | null
  additionalContext?: Record<string, any>
}

class LokiLogger {
  private readonly errorLogUrl: string
  private readonly batchSize = 10
  private readonly flushInterval = 5000 // 5 seconds
  private logQueue: ErrorLogEntry[] = []
  private flushTimer: NodeJS.Timeout | null = null

  constructor() {
    // Determine error log URL based on environment
    this.errorLogUrl = this.getErrorLogUrl()

    // Set up periodic flushing
    this.startPeriodicFlush()

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush()
      })
    }
  }

  private getErrorLogUrl(): string {
    // Check for environment variable first
    if (import.meta.env.VITE_ERROR_LOG_URL) {
      return import.meta.env.VITE_ERROR_LOG_URL
    }

    // Use local URL for development
    if (import.meta.env.MODE === 'development' || window.location.hostname === 'localhost') {
      return 'http://localhost:8080/error-logs/log'
    }

    // Use production URL for production
    return 'https://metrics.tryloop.ai/error-logs/log'
  }

  private startPeriodicFlush() {
    this.flushTimer = setInterval(() => {
      if (this.logQueue.length > 0) {
        this.flush()
      }
    }, this.flushInterval)
  }

  private getCurrentUserEmail(): string | null {
    try {
      // First try to get from localStorage/sessionStorage
      const userData = getSession('data')
      if (userData?.email) {
        return userData.email
      }
      // Fallback to auth.currentUser
      const user = auth?.currentUser
      return user?.email || null
    } catch {
      return null
    }
  }

  private getCurrentUserOrg(): string | null {
    try {
      const userData = getSession('data')
      return userData?.org || null
    } catch (error) {
      return null
    }
  }

  private getCurrentModule(): string {
    try {
      const moduleFromPath = getModuleFromPathname(location.pathname) || LoopModule.BASE
      return moduleFromPath
    } catch {
      return 'Unknown'
    }
  }

  public logError(
    errorMessage: string,
    errorType: ErrorLogEntry['errorType'] = 'unknown',
    options: {
      apiUrl?: string | null
      statusCode?: number | null
      additionalContext?: Record<string, any>
      orgOverride?: string | null
      moduleOverride?: string
    } = {}
  ) {
    const org = this.getCurrentUserOrg()
    const email = this.getCurrentUserEmail()
    const module = this.getCurrentModule()

    const entry: ErrorLogEntry = {
      email: email,
      apiUrl: options.apiUrl || null,
      pageUrl: window.location.href,
      errorMessage: errorMessage.substring(0, 1000), // Limit message length
      statusCode: options.statusCode || null,
      errorType,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      module: module,
      org: org,
      additionalContext: options.additionalContext
    }

    this.logQueue.push(entry)

    // Flush if batch size is reached
    if (this.logQueue.length >= this.batchSize) {
      this.flush()
    }
  }

  public logApiError(
    url: string,
    method: string,
    statusCode: number,
    errorMessage: string,
    responseData?: any,
    orgOverride?: string | null,
    moduleOverride?: string
  ) {
    this.logError(errorMessage, 'api', {
      apiUrl: `${method.toUpperCase()} ${url}`,
      statusCode,
      additionalContext: {
        method,
        responseData: responseData ? JSON.stringify(responseData).substring(0, 500) : null
      },
      orgOverride,
      moduleOverride
    })
  }

  public logClientError(
    error: Error | string,
    componentName?: string,
    additionalContext?: Record<string, any>,
    orgOverride?: string | null,
    moduleOverride?: string
  ) {
    const errorMessage = error instanceof Error ? error.message : error
    const stack = error instanceof Error ? error.stack : undefined

    this.logError(errorMessage, 'client', {
      additionalContext: {
        componentName,
        stack: stack?.substring(0, 500),
        ...additionalContext
      },
      orgOverride,
      moduleOverride
    })
  }

  public logUnhandledError(error: Error | ErrorEvent, source?: string) {
    let errorMessage: string
    let stack: string | undefined

    if (error instanceof ErrorEvent) {
      errorMessage = error.message
      stack = `${error.filename}:${error.lineno}:${error.colno}`
    } else {
      errorMessage = error.message
      stack = error.stack
    }

    this.logError(errorMessage, 'unhandled', {
      additionalContext: {
        source,
        stack: stack?.substring(0, 500)
      }
    })
  }

  public logAuthEvent(eventType: string, data: Record<string, any>) {
    // Log authentication events as a special type of client error
    this.logError(`Auth Event: ${eventType}`, 'client', {
      additionalContext: {
        eventType,
        ...data
      }
    })
  }

  private async flush() {
    if (this.logQueue.length === 0) return

    const logsToSend = [...this.logQueue]
    this.logQueue = []

    // Send each log entry to the backend (backend will batch them for Loki)
    for (const log of logsToSend) {
      try {
        const response = await fetch(this.errorLogUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(log),
          credentials: 'omit', // Don't send cookies to avoid CORS issues
          mode: 'cors' // Explicitly set CORS mode
        })

        if (!response.ok) {
          console.error('Failed to send error log:', response.status, response.statusText)
          // In development, log the error but don't retry to avoid spamming
          if (import.meta.env.MODE === 'development') {
            console.warn('Error logging failed in development. Make sure fe-metrics service is running.')
          }
        }
      } catch (error: any) {
        // Handle CORS errors gracefully in development
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          if (import.meta.env.MODE === 'development') {
            console.warn('Error log endpoint not reachable. Make sure fe-metrics service is running on localhost:8080')
          } else {
            console.error('Error sending logs to backend:', error)
          }
        } else {
          console.error('Error sending logs to backend:', error)
        }
      }
    }
  }

  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    this.flush()
  }
}

// Create singleton instance
export const lokiLogger = new LokiLogger()
